import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { getFinanceDashboard, FinanceDashboardData } from '../services/subscriptionService';

function FinancesDashboard(): React.ReactElement {
  const { token } = useAuth();
  const [data, setData] = useState<FinanceDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      if (!token) return;
      try {
        setLoading(true);
        setError(null);
        const result = await getFinanceDashboard(token);
        setData(result);
      } catch (err: any) {
        console.error('Error loading finance dashboard:', err);
        setError('Failed to load finance dashboard data');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [token]);

  const formatCurrency = (value: number) =>
    `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatMonthLabel = (yyyyMm: string) => {
    const [year, month] = yyyyMm.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleString('en-US', { month: 'short' });
  };

  const getMrrGrowth = (): number | null => {
    if (!data || !data.mrr_trend || data.mrr_trend.length < 2) return null;
    const first = data.mrr_trend[0].mrr;
    const last = data.mrr_trend[data.mrr_trend.length - 1].mrr;
    if (first === 0) return null;
    return ((last - first) / first) * 100;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'trial': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'suspended': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'cancelled': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getStatusCardColor = (status: string) => {
    switch (status) {
      case 'active': return 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20';
      case 'trial': return 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20';
      case 'suspended': return 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20';
      case 'cancelled': return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20';
      default: return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const getStatusTextColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-700 dark:text-green-400';
      case 'trial': return 'text-amber-700 dark:text-amber-400';
      case 'suspended': return 'text-red-700 dark:text-red-400';
      case 'cancelled': return 'text-gray-700 dark:text-gray-400';
      default: return 'text-gray-700 dark:text-gray-400';
    }
  };

  const mrrGrowth = getMrrGrowth();

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-red-800 dark:text-red-300">{error}</h3>
          </div>
        </div>
      </Layout>
    );
  }

  if (!data) return <Layout><div className="p-6">No data available.</div></Layout>;

  const lineChartData = [
    {
      id: 'MRR',
      color: '#4f46e5',
      data: data.mrr_trend.map((item) => ({
        x: formatMonthLabel(item.month),
        y: item.mrr,
      })),
    },
  ];

  const barChartData = data.revenue_by_role.map((item) => ({
    role: item.role,
    revenue: item.total,
  }));

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-white">Finances Dashboard</h1>
          <p className="text-indigo-100 mt-1">Revenue metrics and financial overview</p>
        </div>

        {/* KPI Row 1 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Monthly Recurring Revenue</p>
            <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">{formatCurrency(data.mrr)}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Annual Recurring Revenue</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">{formatCurrency(data.arr)}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Paying Companies</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{data.paying_companies}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Revenue / Company</p>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">{formatCurrency(data.avg_revenue_per_company)}</p>
          </div>
        </div>

        {/* KPI Row 2 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Expected MRR</p>
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">{formatCurrency(data.expected_mrr)}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">If all trials convert</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Trial Companies</p>
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">{data.trial_companies}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Billable Users</p>
            <p className="text-2xl font-bold text-slate-600 dark:text-slate-400 mt-1">{data.total_billable_users}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">MRR Growth</p>
            {mrrGrowth !== null ? (
              <p className={`text-2xl font-bold mt-1 ${mrrGrowth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {mrrGrowth >= 0 ? '+' : ''}{mrrGrowth.toFixed(1)}%
              </p>
            ) : (
              <p className="text-2xl font-bold text-gray-400 dark:text-gray-500 mt-1">--</p>
            )}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Trend period</p>
          </div>
        </div>

        {/* MRR Trend Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">MRR Trend</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Monthly recurring revenue over the last 12 months</p>
          </div>
          <div style={{ height: 400 }} className="p-4">
            {data.mrr_trend.length > 0 ? (
              <ResponsiveLine
                data={lineChartData}
                margin={{ top: 20, right: 30, bottom: 50, left: 80 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
                curve="monotoneX"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Month',
                  legendOffset: 40,
                  legendPosition: 'middle',
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'MRR ($)',
                  legendOffset: -65,
                  legendPosition: 'middle',
                  format: (value) => `$${Number(value).toLocaleString()}`,
                }}
                colors={['#4f46e5']}
                pointSize={8}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                enableArea={true}
                areaOpacity={0.1}
                useMesh={true}
                tooltip={({ point }) => (
                  <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                    <strong className="text-gray-900 dark:text-gray-100">{String(point.data.x)}</strong>
                    <div className="text-indigo-600 dark:text-indigo-400 font-medium">
                      {formatCurrency(Number(point.data.y))}
                    </div>
                  </div>
                )}
                theme={{
                  axis: {
                    ticks: {
                      text: { fill: '#6b7280' },
                    },
                    legend: {
                      text: { fill: '#6b7280' },
                    },
                  },
                  grid: {
                    line: { stroke: '#e5e7eb', strokeWidth: 1 },
                  },
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="mt-2">No trend data available yet</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Revenue by Role Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Revenue by Role</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total monthly revenue contribution per user role</p>
          </div>
          <div style={{ height: 300 }} className="p-4">
            {barChartData.length > 0 ? (
              <ResponsiveBar
                data={barChartData}
                keys={['revenue']}
                indexBy="role"
                layout="horizontal"
                margin={{ top: 10, right: 30, bottom: 50, left: 140 }}
                padding={0.3}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={['#4f46e5', '#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444']}
                colorBy="indexValue"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Revenue ($)',
                  legendPosition: 'middle',
                  legendOffset: 40,
                  format: (value) => `$${Number(value).toLocaleString()}`,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={{ from: 'color', modifiers: [['brighter', 3]] }}
                tooltip={({ indexValue, value }) => (
                  <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                    <strong className="text-gray-900 dark:text-gray-100">{indexValue}</strong>
                    <div className="text-indigo-600 dark:text-indigo-400 font-medium">
                      {formatCurrency(Number(value))}
                    </div>
                  </div>
                )}
                theme={{
                  axis: {
                    ticks: {
                      text: { fill: '#6b7280' },
                    },
                    legend: {
                      text: { fill: '#6b7280' },
                    },
                  },
                  grid: {
                    line: { stroke: '#e5e7eb', strokeWidth: 1 },
                  },
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                <p>No role revenue data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Status Breakdown Cards */}
        {data.status_breakdown.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {data.status_breakdown.map((item) => (
              <div
                key={item.status}
                className={`rounded-lg border p-4 ${getStatusCardColor(item.status)}`}
              >
                <p className={`text-sm font-medium capitalize ${getStatusTextColor(item.status)}`}>{item.status}</p>
                <p className={`text-2xl font-bold mt-1 ${getStatusTextColor(item.status)}`}>{item.count}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">companies</p>
              </div>
            ))}
          </div>
        )}

        {/* Top Companies Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Top Companies by Revenue</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Highest revenue-generating companies</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Discount</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Monthly Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {data.top_companies.slice(0, 15).map((company, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{company.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{company.users}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(company.status)}`}>
                        {company.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {company.discount > 0 ? `${company.discount}%` : '--'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-gray-900 dark:text-gray-100">
                      {formatCurrency(company.mrr)}
                    </td>
                  </tr>
                ))}
                {data.top_companies.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <p className="mt-2">No company data available</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default FinancesDashboard;
